//TODO : Refactor after ai
/**
 * Modul 1 – Sales Cockpit: „Was muss ich heute verkaufen?“
 * Für Disponent, Gruppenführer und Innendienst zeigt die Startseite stattdessen ihren Arbeitsvorrat.
 */
import { useNavigate } from "react-router-dom";
import { useDatenStore } from "@app/store";
import { datensaetze, rolle as rolleVon } from "@shared/demo-data/viacid";
import {
  angebotsSumme,
  crossSellingPotenziale,
  datumCH,
  fr,
  naechsteSaison,
  str,
  tageSeit,
  ziel,
} from "./lib";
import { Karte, Kennzahl, Knopf, Pille, Seite, Zeile } from "./ui";

export function CockpitPage() {
  const b = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const nav = useNavigate();
  const r = rolleVon(rolle);

  if (rolle === "DI") return <VorratDisposition />;
  if (rolle === "GF" || rolle === "SU") return <VorratBaustelle />;
  if (rolle === "ID") return <VorratInnendienst />;
  if (rolle === "PM") return <VorratProduktion />;
  if (rolle === "KO" || rolle === "HA" || rolle === "TD")
    return <VorratExtern rolle={rolle} />;

  const { saison, wochen, laeuft } = naechsteSaison();
  const potenziale = crossSellingPotenziale(b);
  const angebote = datensaetze(b, "angebot");
  const offen = angebote.filter((a) =>
    ["Versendet", "Änderung gewünscht", "In Erstellung"].includes(
      a.status ?? ""
    )
  );
  const volumen = offen.reduce((s, a) => s + angebotsSumme(b, a.id), 0);
  const alt = offen.filter((a) => tageSeit(str(a.werte.angebotsdatum)) > 14);
  const chancen = datensaetze(b, "verkaufschance");
  const gewonnen = chancen.filter((c) => c.status === "Gewonnen").length;
  const verloren = chancen.filter((c) => c.status === "Verloren").length;
  const quote =
    gewonnen + verloren
      ? Math.round((100 * gewonnen) / (gewonnen + verloren))
      : 0;
  const aufgaben = datensaetze(b, "aufgabe").filter(
    (a) => a.status === "Offen"
  );
  const offeneChancen = chancen.filter((c) =>
    ["Identifiziert", "Qualifiziert"].includes(c.status ?? "")
  );
  const ohneFollowUp = offeneChancen.filter(
    (c) =>
      !aufgaben.some((a) =>
        [a.bezuege["aufgabe-betrifft-verkaufschance"]].flat().includes(c.id)
      )
  );

  return (
    <Seite
      titel={`Guten Tag${r?.persona ? "" : ""} – Sales Cockpit`}
      untertitel="Pipeline, offene Angebote, Follow-ups und saisonale Verkaufschancen"
    >
      <div className="vc-grid c4" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Offene Angebote"
          wert={offen.length}
          hint={`Volumen ${fr(volumen)}`}
        />
        <Kennzahl
          label="Älter als 14 Tage"
          wert={alt.length}
          hint="nachfassen"
          ton={alt.length ? "warn" : undefined}
        />
        <Kennzahl
          label="Fehlende Follow-ups"
          wert={ohneFollowUp.length}
          hint="Chancen ohne offene Aufgabe"
          ton={ohneFollowUp.length ? "warn" : undefined}
        />
        <Kennzahl
          label="Erfolgsquote"
          wert={`${quote} %`}
          hint={`${gewonnen} gewonnen · ${verloren} verloren`}
          ton="accent"
        />
      </div>

      <div className="vc-grid main-side">
        <div className="vc-grid">
          <Karte
            titel={`${saison.name}-Saison ${laeuft ? "läuft seit" : "beginnt in"} ${wochen} Wochen`}
            sub={`${saison.massnahmen.join(", ")} · Baustoffe: ${saison.baustoffe.join(", ")}`}
            rechts={<Pille farbe="orange">Saisonimpuls</Pille>}
          >
            <div className="vc-list">
              {potenziale.map((p) => (
                <Zeile
                  key={p.kunde.id}
                  titel={p.kunde.titel}
                  meta={p.grund}
                  rechts={
                    <>
                      <Pille farbe="teal">Potenzial {p.art}</Pille>
                      <Knopf onClick={() => nav(`/app/kunden/${p.kunde.id}`)}>
                        Kunde öffnen
                      </Knopf>
                    </>
                  }
                />
              ))}
              {potenziale.length === 0 && (
                <div className="sub">Keine offenen Potenziale.</div>
              )}
            </div>
          </Karte>

          <Karte titel="Offene Angebote" sub="Nach Alter – die ältesten zuerst">
            <table className="vc-table">
              <thead>
                <tr>
                  <th>Angebot</th>
                  <th>Kunde</th>
                  <th>Alter</th>
                  <th className="num">Summe</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...offen]
                  .sort(
                    (x, y) =>
                      tageSeit(str(y.werte.angebotsdatum)) -
                      tageSeit(str(x.werte.angebotsdatum))
                  )
                  .map((a) => (
                    <tr
                      key={a.id}
                      className="link"
                      onClick={() => nav(`/app/angebote/${a.id}`)}
                    >
                      <td>
                        {a.kennung} · {a.titel}
                      </td>
                      <td>{ziel(b, a, "angebot-an-kunde", "kunde")?.titel}</td>
                      <td>{tageSeit(str(a.werte.angebotsdatum))} Tage</td>
                      <td className="num">{fr(angebotsSumme(b, a.id))}</td>
                      <td>
                        <Pille status={a.status} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Karte>
        </div>

        <div className="vc-grid">
          <Karte
            titel="Pipeline"
            sub={`${offeneChancen.length} offene Verkaufschancen`}
          >
            <div className="vc-list">
              {offeneChancen.map((c) => (
                <Zeile
                  key={c.id}
                  titel={c.titel}
                  meta={`${ziel(b, c, "verkaufschance-betrifft-kunde", "kunde")?.titel ?? ""} · ${str(c.werte.saisonfenster)}`}
                  rechts={<Pille status={c.status} />}
                  zu={`/app/chancen/${c.id}`}
                />
              ))}
            </div>
          </Karte>
          <Karte titel="Meine Aufgaben" sub={`${aufgaben.length} offen`}>
            <div className="vc-list">
              {aufgaben.map((a) => (
                <Zeile
                  key={a.id}
                  titel={a.titel}
                  meta={`fällig ${datumCH(a.werte.faelligAm)} · ${str(a.werte.aufgabenart)}`}
                  rechts={
                    <Pille
                      farbe={tageSeit(str(a.werte.faelligAm)) > 0 ? "red" : ""}
                    >
                      {str(a.werte.prioritaet) || "normal"}
                    </Pille>
                  }
                  zu={`/app/daten/aufgabe/${a.id}`}
                />
              ))}
            </div>
          </Karte>
        </div>
      </div>
    </Seite>
  );
}

function VorratDisposition() {
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  const auftraege = datensaetze(b, "auftrag").filter(
    (a) =>
      a.werte.auftragsart === "Dienstleistung" &&
      ["Erteilt", "Freigegeben"].includes(a.status ?? "")
  );
  const einsaetze = datensaetze(b, "einsatz").filter((e) =>
    ["Geplant", "Terminiert"].includes(e.status ?? "")
  );
  const abwesenheiten = datensaetze(b, "abwesenheit").filter(
    (a) => a.status === "Gemeldet"
  );
  return (
    <Seite
      titel="Disposition – heute"
      untertitel="Aufträge ohne Einsatz, geplante Einsätze, gemeldete Abwesenheiten"
    >
      <div className="vc-grid c3" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Aufträge zu planen"
          wert={auftraege.length}
          ton={auftraege.length ? "warn" : undefined}
        />
        <Kennzahl label="Geplante Einsätze" wert={einsaetze.length} />
        <Kennzahl
          label="Abwesenheiten zu bestätigen"
          wert={abwesenheiten.length}
        />
      </div>
      <Karte
        titel="Zur Plantafel"
        rechts={
          <Knopf art="primary" onClick={() => nav("/app/planung")}>
            Plantafel öffnen
          </Knopf>
        }
      >
        <div className="vc-list">
          {auftraege.map((a) => (
            <Zeile
              key={a.id}
              titel={`${a.kennung} · ${a.titel}`}
              meta={`${ziel(b, a, "auftrag-von-kunde", "kunde")?.titel} · ${datumCH(a.werte.ausfuehrungszeitraumVon)} – ${datumCH(a.werte.ausfuehrungszeitraumBis)}`}
              rechts={<Pille status={a.status} />}
              zu={`/app/auftraege/${a.id}`}
            />
          ))}
        </div>
      </Karte>
    </Seite>
  );
}

function VorratBaustelle() {
  const b = useDatenStore((s) => s.bestand);
  const einsaetze = datensaetze(b, "einsatz").filter(
    (e) => !["Ausgeführt", "Ausführung abgesagt"].includes(e.status ?? "")
  );
  return (
    <Seite
      titel="Meine Baustellen"
      untertitel="Einsätze, die noch nicht abgeschlossen sind"
    >
      <div className="vc-list vc-card">
        {einsaetze.map((e) => (
          <Zeile
            key={e.id}
            titel={e.titel}
            meta={`${datumCH(e.werte.einsatzdatum)} · ${str(e.werte.zeitslot)} · ${ziel(b, e, "einsatz-am-strassenobjekt", "strassenobjekt")?.titel ?? ""}`}
            rechts={<Pille status={e.status} />}
            zu={`/app/baustelle/${e.id}`}
          />
        ))}
        {einsaetze.length === 0 && (
          <div className="sub">Keine offenen Einsätze.</div>
        )}
      </div>
    </Seite>
  );
}

function VorratInnendienst() {
  const b = useDatenStore((s) => s.bestand);
  const bestellungen = datensaetze(b, "bestellung").filter(
    (x) => x.status === "Eingegangen"
  );
  const abrechnung = datensaetze(b, "auftrag").filter(
    (a) => a.status === "Abrechnungsbereit"
  );
  const ausmass = datensaetze(b, "ausmassprotokoll").filter(
    (a) => a.status !== "Freigegeben"
  );
  return (
    <Seite
      titel="Innendienst – heute"
      untertitel="Bestelleingang, Ausmassfreigaben, Abrechnung"
    >
      <div className="vc-grid c3" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Bestellungen eingegangen"
          wert={bestellungen.length}
          ton={bestellungen.length ? "warn" : undefined}
        />
        <Kennzahl label="Ausmass offen" wert={ausmass.length} />
        <Kennzahl
          label="Abrechnungsbereit"
          wert={abrechnung.length}
          ton="accent"
        />
      </div>
      <div className="vc-grid c2">
        <Karte titel="Bestellungen">
          <div className="vc-list">
            {bestellungen.map((x) => (
              <Zeile
                key={x.id}
                titel={x.titel}
                meta={`${str(x.werte.eingangskanal)} · ${datumCH(x.werte.eingangsdatum)}`}
                rechts={<Pille status={x.status} />}
                zu={`/app/daten/bestellung/${x.id}`}
              />
            ))}
          </div>
        </Karte>
        <Karte titel="Abrechnungsbereite Aufträge">
          <div className="vc-list">
            {abrechnung.map((a) => (
              <Zeile
                key={a.id}
                titel={`${a.kennung} · ${a.titel}`}
                meta={ziel(b, a, "auftrag-von-kunde", "kunde")?.titel}
                rechts={<Pille status={a.status} />}
                zu={`/app/auftraege/${a.id}`}
              />
            ))}
          </div>
        </Karte>
      </div>
    </Seite>
  );
}

/** Sicht von aussen (simuliert): Kunde, Händler und Spedition sehen nur, was sie selbst entscheiden müssen. */
function VorratExtern({ rolle }: { rolle: string }) {
  const b = useDatenStore((s) => s.bestand);
  const angebote = datensaetze(b, "angebot").filter(
    (a) => a.status === "Versendet"
  );
  const ausmasse = datensaetze(b, "ausmassprotokoll").filter(
    (a) => a.status === "Zur Prüfung übermittelt"
  );
  const lieferungen = datensaetze(b, "lieferung").filter(
    (l) => l.status === "Versendet"
  );
  const rechnungen = datensaetze(b, "rechnung").filter(
    (r) => r.status === "Versendet" || r.status === "Gemahnt"
  );
  const titel = rolle === "TD" ? "Zustellungen" : "Ihre Vorgänge bei Viacid";
  const untertitel =
    rolle === "TD"
      ? "Sendungen, deren Zustellung noch zu bestätigen ist"
      : "Angebote, Ausmasse und Rechnungen, die auf Ihre Entscheidung warten";
  return (
    <Seite titel={titel} untertitel={untertitel}>
      {rolle !== "TD" && (
        <Karte titel="Angebote zur Entscheidung">
          <div className="vc-list">
            {angebote.map((a) => (
              <Zeile
                key={a.id}
                titel={`${a.kennung} · ${a.titel}`}
                meta={`${datumCH(a.werte.angebotsdatum)} · gültig bis ${datumCH(a.werte.gueltigBis)}`}
                rechts={<Pille status={a.status} />}
                zu={`/app/angebote/${a.id}`}
              />
            ))}
            {angebote.length === 0 && (
              <div className="sub">Keine offenen Angebote.</div>
            )}
          </div>
        </Karte>
      )}
      {rolle === "KO" && (
        <Karte titel="Ausmasse zur Freigabe">
          <div className="vc-list">
            {ausmasse.map((a) => (
              <Zeile
                key={a.id}
                titel={`${a.kennung} · ${a.titel}`}
                meta={datumCH(a.werte.ausmassdatum)}
                rechts={<Pille status={a.status} />}
                zu={`/app/daten/ausmassprotokoll/${a.id}`}
              />
            ))}
            {ausmasse.length === 0 && (
              <div className="sub">Kein Ausmass wartet auf Freigabe.</div>
            )}
          </div>
        </Karte>
      )}
      {rolle === "TD" && (
        <Karte titel="Sendungen">
          <div className="vc-list">
            {lieferungen.map((l) => (
              <Zeile
                key={l.id}
                titel={`${l.kennung} · ${l.titel}`}
                meta={`${datumCH(l.werte.liefertermin)} · ${str(l.werte.transportdienstleister)}`}
                rechts={<Pille status={l.status} />}
                zu={`/app/daten/lieferung/${l.id}`}
              />
            ))}
            {lieferungen.length === 0 && (
              <div className="sub">Keine Sendung unterwegs.</div>
            )}
          </div>
        </Karte>
      )}
      {rolle !== "TD" && (
        <Karte titel="Offene Rechnungen">
          <div className="vc-list">
            {rechnungen.map((r) => (
              <Zeile
                key={r.id}
                titel={`${r.kennung} · ${r.titel}`}
                meta={`fällig ${datumCH(r.werte.faelligkeitsdatum)}`}
                rechts={<Pille status={r.status} />}
                zu={`/app/daten/rechnung/${r.id}`}
              />
            ))}
            {rechnungen.length === 0 && (
              <div className="sub">Keine offenen Rechnungen.</div>
            )}
          </div>
        </Karte>
      )}
    </Seite>
  );
}

/** Produktion: Lieferungen rüsten, gemeldetes Material bestätigen. */
function VorratProduktion() {
  const b = useDatenStore((s) => s.bestand);
  const lieferungen = datensaetze(b, "lieferung").filter(
    (l) => l.status === "Veranlasst" || l.status === "Bereitgestellt"
  );
  const material = datensaetze(b, "materialbewegung").filter(
    (m) => m.status === "Gemeldet"
  );
  return (
    <Seite
      titel="Lager und Rüsten"
      untertitel="Lieferungen bereitstellen, Materialmeldungen bestätigen"
    >
      <Karte titel="Lieferungen zum Rüsten">
        <div className="vc-list">
          {lieferungen.map((l) => (
            <Zeile
              key={l.id}
              titel={`${l.kennung} · ${l.titel}`}
              meta={`${datumCH(l.werte.liefertermin)} · ${str(l.werte.versandart)}`}
              rechts={<Pille status={l.status} />}
              zu={`/app/daten/lieferung/${l.id}`}
            />
          ))}
          {lieferungen.length === 0 && (
            <div className="sub">Nichts zu rüsten.</div>
          )}
        </div>
      </Karte>
      <Karte titel="Materialmeldungen">
        <div className="vc-list">
          {material.map((m) => (
            <Zeile
              key={m.id}
              titel={m.titel}
              meta={m.untertitel ?? ""}
              rechts={<Pille status={m.status} />}
              zu={`/app/daten/materialbewegung/${m.id}`}
            />
          ))}
          {material.length === 0 && (
            <div className="sub">Keine offenen Meldungen.</div>
          )}
        </div>
      </Karte>
    </Seite>
  );
}
